/**
 * Desktop guide lines of the "A simple vista" banner (`sections/thcc-banner.liquid`).
 *
 * Figma draws three lime lines that start right after each data row's text and converge on the package.
 * The start points depend on the rendered text width, so they are measured here and drawn as an inline
 * SVG sized to the card (all sizes scale with card width / 1320). On mobile the guides are pure CSS and
 * this module does nothing. Everything is built with DOM APIs; no data is parsed as HTML.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';
const DESKTOP = window.matchMedia('(min-width: 750px)');
const LIME = '#d4ee3b';

/**
 * @param {string} name
 * @param {Record<string, string | number>} attrs
 * @returns {SVGElement}
 */
function svgEl(name, attrs) {
  const el = document.createElementNS(SVG_NS, name);
  for (const [key, value] of Object.entries(attrs)) el.setAttribute(key, String(value));
  return el;
}

/** @param {HTMLElement} card */
function draw(card) {
  const svg = card.querySelector('.thcc-banner__lines');
  if (!(svg instanceof SVGElement)) return;

  svg.replaceChildren();
  if (!DESKTOP.matches || card.hidden) return;

  const box = card.getBoundingClientRect();
  if (box.width === 0) return;

  const u = box.width / 1320;
  const styles = getComputedStyle(card);
  const cx = (parseFloat(styles.getPropertyValue('--thcc-banner-x')) / 100) * box.width;
  const cy = (parseFloat(styles.getPropertyValue('--thcc-banner-y')) / 100) * box.height;
  if (Number.isNaN(cx) || Number.isNaN(cy)) return;

  svg.setAttribute('viewBox', `0 0 ${box.width} ${box.height}`);

  for (const row of card.querySelectorAll('.thcc-banner__dato')) {
    const value = row.querySelector('.thcc-banner__valor');
    const texts = row.querySelector('.thcc-banner__textos');
    if (!value || !texts) continue;

    const v = value.getBoundingClientRect();
    const t = texts.getBoundingClientRect();
    const x0 = t.right - box.left + 10 * u;
    const y0 = v.top - box.top + v.height * 0.5915;

    svg.append(
      svgEl('path', {
        d: `M${x0} ${y0}H${x0 + 36 * u}L${cx} ${cy}`,
        fill: 'none',
        stroke: LIME,
        'stroke-width': 1.6 * u,
        'stroke-linejoin': 'round',
      }),
      svgEl('circle', { cx: x0, cy: y0, r: 2.5 * u, fill: LIME })
    );
  }

  svg.append(
    svgEl('circle', { cx, cy, r: 11 * u, fill: 'none', stroke: LIME, 'stroke-width': 1.6 * u }),
    svgEl('circle', { cx, cy, r: 4.5 * u, fill: LIME })
  );
}

/** @type {ResizeObserver | undefined} */
let observer;

function run() {
  observer ??= new ResizeObserver((entries) => {
    for (const entry of entries) {
      if (entry.target instanceof HTMLElement) draw(entry.target);
    }
  });

  for (const card of document.querySelectorAll('[data-thcc-banner-card]')) {
    if (!(card instanceof HTMLElement)) continue;
    observer.observe(card);
    draw(card);
  }
}

DESKTOP.addEventListener('change', run);
document.addEventListener('shopify:section:load', run);
// Fonts change the text widths after first paint.
document.fonts?.ready.then(run);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', run, { once: true });
} else {
  run();
}
